import { useState, useEffect } from 'react';
import { useAuth } from './context/AuthContext';
import { Layout } from './components/layout/Layout';
import { Login } from './pages/Login';
import LandingPage from './pages/Landing';
import Dashboard from './pages/Dashboard';
import QuotationsPage from './pages/Quotations/Index';
import EditQuotationPage from './pages/Quotations/Edit';
import PartiesPage from './pages/Parties/Index';
import CompaniesPage from './pages/Companies/Index';
import ProductsPage from './pages/Products/Index';
import PriceListPage from './pages/PriceList/Index';
import ReportsPage from './pages/Reports/Index';
import ProjectTrackerPage from './pages/Tracker/Index';
import ImportPage from './pages/Import/Index';
import HelpPage from './pages/Help/Index';
import LatticeCalculatorPage from './pages/Lattice/Index';
import StockInventoryPage from './pages/Stock/Index';
import MarketingPortal from './marketing/MarketingPortal';
import AdminLayout from './admin/AdminLayout';
import TeamManagementPage from './pages/Team/Index';
import SecretAuditPortal from './pages/SecretAuditPortal/Index';
import { hasPageAccess } from './lib/userValidation';

function isHiddenAuditRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = (window.location.pathname || '').toLowerCase();
  const hash = (window.location.hash || '').toLowerCase();
  const search = (window.location.search || '').toLowerCase();
  return (
    path.includes('jfdbfefhbrjhewbfrjh') ||
    hash.includes('jfdbfefhbrjhewbfrjh') ||
    search.includes('jfdbfefhbrjhewbfrjh')
  );
}

function App() {
  const { user, profile, loading, isExecutive } = useAuth();
  const [currentPage, setCurrentPage] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      if (isHiddenAuditRoute()) {
        return 'hidden_audit_portal';
      }
      if (window.location.pathname.startsWith('/admin')) {
        return 'admin';
      }
    }
    return 'dashboard';
  });
  const [editingQuotationId, setEditingQuotationId] = useState<number | null>(null);
  const [showLogin, setShowLogin] = useState(false);

  // Sync with browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      if (isHiddenAuditRoute()) {
        setCurrentPage('hidden_audit_portal');
      } else if (window.location.pathname.startsWith('/admin')) {
        setCurrentPage('admin');
      } else if (currentPage === 'admin' || currentPage === 'hidden_audit_portal') {
        setCurrentPage('dashboard');
      }
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handlePopState);
    };
  }, [currentPage]);

  const handleNavigate = (page: string) => {
    if (page === 'admin') {
      window.history.pushState({}, '', '/admin');
      setCurrentPage('admin');
    } else {
      if (window.location.pathname.startsWith('/admin') || isHiddenAuditRoute()) {
        window.history.pushState({}, '', '/');
      }
      setCurrentPage(page);
    }
  };

  // Render Hidden Secret Audit Portal if URL matches secret token
  if (currentPage === 'hidden_audit_portal') {
    return <SecretAuditPortal />;
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white">
        <div className="h-12 w-12 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center text-2xl animate-pulse shadow-lg mb-4">
          ⚙️
        </div>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-400 border-t-transparent mb-3"></div>
        <span className="text-sm font-medium text-slate-300">Loading Sai Krishna Portal...</span>
      </div>
    );
  }

  if (!user) {
    if (showLogin) {
      return (
        <Login
          onSuccess={() => {
            setShowLogin(false);
            if (window.location.pathname.startsWith('/admin')) {
              setCurrentPage('admin');
            } else {
              setCurrentPage('dashboard');
            }
          }}
        />
      );
    }
    return <LandingPage onLogin={() => setShowLogin(true)} />;
  }

  const isAdmin = profile?.role === 'admin' || profile?.role === 'executive' || isExecutive;

  // Render Dedicated Admin Dashboard (/admin)
  if (currentPage === 'admin') {
    if (!isAdmin) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 font-sans">
          <div className="max-w-md w-full bg-slate-900 rounded-2xl p-8 border border-red-500/30 text-center shadow-2xl">
            <div className="h-16 w-16 bg-red-500/20 text-red-400 rounded-2xl mx-auto flex items-center justify-center text-3xl mb-4 border border-red-500/30">
              ⛔
            </div>
            <h2 className="text-xl font-bold text-white">Administrative Access Restricted</h2>
            <p className="text-xs text-slate-300 mt-2 leading-relaxed">
              The <span className="font-mono text-amber-400">/admin</span> command center is restricted to System Administrators.
              Your account ({user.email}) does not have administrative rights.
            </p>
            <div className="mt-6">
              <button
                onClick={() => handleNavigate('dashboard')}
                className="w-full px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs rounded-xl shadow-lg transition-all"
              >
                ← Return to Staff Operational Portal
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <AdminLayout
        onBackToPortal={() => handleNavigate('dashboard')}
      />
    );
  }

  if (currentPage === 'marketing') {
    return <MarketingPortal onBackToPortal={() => handleNavigate('dashboard')} />;
  }
  
  const effectiveRole = isExecutive ? (profile?.role === 'admin' ? 'admin' : 'executive') : (profile?.role || 'sales_rep');

  const renderPage = () => {
    // Route-level RBAC barrier: check permissions for the current page
    if (!hasPageAccess(effectiveRole, currentPage) && !isExecutive) {
      return (
        <div className="min-h-[50vh] flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white rounded-2xl p-8 border border-red-200 text-center shadow-sm">
            <div className="h-14 w-14 bg-red-50 text-red-600 rounded-2xl mx-auto flex items-center justify-center text-2xl mb-4 border border-red-100">
              ⛔
            </div>
            <h3 className="text-lg font-bold text-gray-900">Access Restricted</h3>
            <p className="text-xs text-gray-600 mt-2 leading-relaxed">
              Your role (<span className="font-semibold text-blue-600 capitalize">{effectiveRole.replace('_', ' ')}</span>) does not have authorization to access the <strong>{currentPage}</strong> module.
            </p>
            <div className="mt-6">
              <button
                onClick={() => handleNavigate('dashboard')}
                className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-xl shadow-sm transition-colors"
              >
                ← Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    switch (currentPage) {
      case 'dashboard':
        return <Dashboard onNavigate={handleNavigate} />;
      case 'quotations':
        return editingQuotationId ? (
          <EditQuotationPage
            quotationId={editingQuotationId}
            onBack={() => { setEditingQuotationId(null); handleNavigate('quotations'); }}
          />
        ) : (
          <QuotationsPage onEditQuotation={(id) => setEditingQuotationId(id)} />
        );
      case 'parties':
        return <PartiesPage />;
      case 'companies':
        return <CompaniesPage />;
      case 'products':
        return <ProductsPage />;
      case 'pricelist':
        return <PriceListPage />;
      case 'reports':
        return <ReportsPage />;
      case 'import':
        return <ImportPage />;
      case 'tracker':
        return <ProjectTrackerPage />;
      case 'lattice':
        return <LatticeCalculatorPage />;
      case 'stock':
        return <StockInventoryPage />;
      case 'team':
        return <TeamManagementPage />;
      case 'help':
        return <HelpPage />;
      default:
        return <Dashboard onNavigate={handleNavigate} />;
    }
  };
  
  return (
    <Layout currentPage={currentPage} onNavigate={handleNavigate}>
      {renderPage()}
    </Layout>
  );
}

export default App;
